import { useFormContext, Controller } from 'react-hook-form';
import { InputBaseProps, TextFieldProps } from '@mui/material';

import { TextField } from '../basics';
import { ChangeEvent } from 'react';
import { filterPasswordCharacters } from '../../utils/passwordPolicy';

type Props = {
  inputProps?: InputBaseProps['inputProps'];
  InputProps?: TextFieldProps['InputProps'];
  disabled?: boolean;
  disabledErrors?: boolean;
  label?: string;
  name: string;
  onlyNumbers?: boolean;
  /**
   * Descarta al escribir lo que la política de contraseñas no admite. Va aquí
   * y no en el schema porque un espacio pegado al final debe desaparecer al
   * capturarlo, no convertirse en un error de validación.
   */
  onlyPasswordChars?: boolean;
  /**
   * Quita los espacios al escribir. Es lo que pide el hallazgo F para la
   * contraseña del ingreso, donde no se puede filtrar por la política: ahí la
   * contraseña ya existe y puede ser anterior a ella.
   */
  withoutSpaces?: boolean;
  isTextArea?: boolean;
  type?: string;
};

const filterNumbers = (val: string) => {
  const re = /^[0-9\b]+$/;
  if (val === '' || re.test(val)) {
    return val;
  }
};

/**
 * Quita todo espacio en blanco, no solo los de los extremos. En una contraseña
 * no hay espacio interno legítimo que conservar, y el que llega pegado cuesta
 * un intento de ingreso.
 */
const filterSpaces = (val: string): string => val.replace(/\s/g, '');

export const FormTextField: React.FC<Props> = ({
  inputProps,
  InputProps,
  disabled,
  disabledErrors,
  label,
  name,
  onlyNumbers,
  onlyPasswordChars,
  withoutSpaces,
  isTextArea = false,
  type,
}): JSX.Element => {
  const { control, clearErrors, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ fieldState: { error }, field: { onChange, value } }) => {
        const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
          const newValue = event.target.value;
          /**
           * `filterNumbers` devuelve `undefined` y descarta el cambio entero
           * cuando algo no cuadra; el de contraseña siempre devuelve un valor,
           * porque quita los caracteres sobrantes y conserva el resto.
           */
          const filteredValue = onlyNumbers
            ? filterNumbers(newValue)
            : onlyPasswordChars
            ? filterPasswordCharacters(newValue)
            : withoutSpaces
            ? filterSpaces(newValue)
            : newValue;

          if (filteredValue !== undefined) {
            clearErrors(name);
            !!onChange && onChange(filteredValue);
            setValue(name, filteredValue);
          }
        };

        return (
          <TextField
            disabled={disabled}
            disabledErrors={disabledErrors}
            error={!!error}
            helperText={!!error ? error.message : null}
            label={label}
            inputProps={inputProps}
            InputProps={InputProps}
            type={type}
            value={value ? value : ''}
            onlyNumbers={onlyNumbers}
            onChange={handleOnChange}
            multiline={isTextArea}
            rows={isTextArea ? 4 : 1}
          />
        );
      }}
    />
  );
};
